import { useEffect, useState } from "react";
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";
import {
  fetchAccountMovements,
  fetchCompanies,
  type Company,
  type Movement,
  type MovementsResponse,
} from "../api";
import { formatIban, formatMoney } from "../lib/grouping";

export function AccountMovements() {
  const { iban: ibanParam } = useParams<{ iban: string }>();
  const location = useLocation();
  const backTo = (location.state as { from?: string } | null)?.from ?? "/cuentas";
  const backLabel = backTo === "/resumen" ? "← Resumen" : "← Cuentas";
  const iban = ibanParam ?? "";

  const [searchParams, setSearchParams] = useSearchParams();
  const [from, setFrom] = useState(searchParams.get("from") ?? "");
  const [to, setTo] = useState(searchParams.get("to") ?? "");
  const [q, setQ] = useState(searchParams.get("q") ?? "");

  const [data, setData] = useState<MovementsResponse | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!iban) return;
    setError(null);
    setLoading(true);
    try {
      const [m, c] = await Promise.all([
        fetchAccountMovements(iban, {
          from: from || undefined,
          to: to || undefined,
          q: q || undefined,
        }),
        fetchCompanies(),
      ]);
      setData(m);
      setCompanies(c.items);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iban]);

  const applyFilters = () => {
    const params = new URLSearchParams();
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    if (q) params.set("q", q);
    setSearchParams(params, { replace: true });
    void load();
  };

  const clearFilters = () => {
    setFrom("");
    setTo("");
    setQ("");
    setSearchParams(new URLSearchParams(), { replace: true });
    setTimeout(() => void load(), 0);
  };

  const companyLabel = (id: string) => {
    const c = companies.find((x) => x.id === id);
    return c ? (c.alias || c.name) : id || "—";
  };

  const currency = data?.account.currency || "EUR";

  return (
    <section className="card">
      <div className="card-head">
        <div>
          <h2>Movimientos · {data?.account.alias || formatIban(iban)}</h2>
          <p className="meta">
            <Link to={backTo}>{backLabel}</Link>
            {"  ·  "}
            <Link to={`/cuentas/${encodeURIComponent(iban)}/apuntes-sap`} state={{ from: backTo }}>
              Apuntes SAP
            </Link>
            {"  ·  "}
            <Link to={`/cuentas/${encodeURIComponent(iban)}/conciliar`} state={{ from: backTo }}>
              Conciliar
            </Link>
            {data?.account && (
              <>
                {"  ·  "}
                <code>{formatIban(data.account.iban)}</code>
                {"  ·  "}
                {data.account.bankName || data.account.bankCode}
                {"  ·  "}
                {companyLabel(data.account.companyId)}
                {"  ·  "}
                Último sync: {data.account.lastSyncedAt
                  ? new Date(data.account.lastSyncedAt).toLocaleString()
                  : "nunca"}
              </>
            )}
          </p>
        </div>
        <button type="button" onClick={load} disabled={loading}>
          {loading ? "Cargando…" : "Recargar"}
        </button>
      </div>

      <div className="form-row" style={{ flexWrap: "wrap" }}>
        <label>
          Desde{" "}
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </label>
        <label>
          Hasta{" "}
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
        </label>
        <input
          placeholder="Buscar en descripción / ordenante / IBAN contrapartida"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") applyFilters();
          }}
          style={{ minWidth: "20rem" }}
        />
        <button type="button" onClick={applyFilters} disabled={loading}>
          Aplicar
        </button>
        <button type="button" onClick={clearFilters} disabled={loading}>
          Limpiar
        </button>
      </div>

      {error && <p className="error">{error}</p>}
      {!error && data === null && !loading && <p>Cargando…</p>}
      {data && data.items.length === 0 && (
        <p className="meta">
          No hay movimientos para esta cuenta. Se rellenarán solos con el
          próximo refresco del banco. Si ya recibiste payloads antes de activar
          esta función, ve a Sin vincular → Reprocesar payloads.
        </p>
      )}
      {data && data.items.length > 0 && (
        <>
          <p className="meta">
            {data.total} movimiento{data.total === 1 ? "" : "s"} en total.
          </p>
          <table className="data">
            <thead>
              <tr>
                <th>Fecha op.</th>
                <th>Fecha valor</th>
                <th>Descripción</th>
                <th>Transferencia</th>
                <th className="num">Importe</th>
                <th className="num">Saldo</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((m) => (
                <MovementRow key={m.uuid} movement={m} currency={currency} />
              ))}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}

function MovementRow({ movement, currency }: { movement: Movement; currency: string }) {
  const td = movement.transferDetails;
  const amountClass = movement.amount < 0 ? "num neg" : "num";
  return (
    <tr>
      <td>{movement.operationDate || <span className="meta">—</span>}</td>
      <td>{movement.valueDate || <span className="meta">—</span>}</td>
      <td>{movement.description || <span className="meta">—</span>}</td>
      <td>
        {td ? (
          <div style={{ fontSize: "0.85em", lineHeight: 1.3 }}>
            {td.concept && <div>{td.concept}</div>}
            {td.senderReceiver && <div className="meta">{td.senderReceiver}</div>}
            {td.accountNumber && <code className="meta">{formatIban(td.accountNumber)}</code>}
          </div>
        ) : (
          <span className="meta">—</span>
        )}
      </td>
      <td className={amountClass}>{formatMoney(movement.amount, currency)}</td>
      <td className="num">
        {movement.balance !== null
          ? formatMoney(movement.balance, currency)
          : <span className="meta">—</span>}
      </td>
    </tr>
  );
}
